import React from "react";

import { CHAR, fitLabel, scaleFor } from "./flowLayout";

export type FlowNode = {
  id: string;
  label: string;
  /** 0 is the leftmost column. */
  column: number;
  /** A series token such as "var(--ds-s1)"; links take their source's. */
  color?: string;
};

export type FlowLink = { source: string; target: string; value: number };

type Sized = FlowNode & { value: number; in: number; out: number };

type Placed = Sized & {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Where this node's bands start: centred when the node is taller. */
  pad: number;
};

const BAR = 6;
const GAP = 12;
const HEAD = 26;
// Below this width the chart turns into ranked lists, one per column.
const NARROW = 560;
// Outer nodes fit one line of label; inner blocks fit a name and an amount.
const MIN_OUTER = 18;
const MIN_INNER = 38;
const TWO_LINES = 30;

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

/** Proportional flows between columns of nodes (a Sankey): where an amount
 * goes, step by step. Nodes keep the order given; put "Other" last. On a
 * narrow card each column becomes a ranked list. */
export function FlowChart({
  nodes,
  links,
  format,
  height = 520,
  label,
  columns,
  unit,
}: {
  nodes: FlowNode[];
  links: FlowLink[];
  format: (v: number) => string;
  height?: number;
  label: string;
  /** A heading over each column, left to right. */
  columns?: string[];
  /** What band width measures ("tokens"), for the caption under the chart. */
  unit?: string;
}) {
  const box = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);
  const [hover, setHover] = React.useState<string | null>(null);
  React.useEffect(() => {
    const el = box.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const live = links.filter((l) => l.value > 0);
  const narrow = width > 0 && width < NARROW;
  const head = columns?.length ? HEAD : 0;
  const layout =
    width > 0 && !narrow
      ? place(nodes, live, width, height, head, format)
      : null;
  const total = layout
    ? [...layout.byId.values()]
        .filter((n) => n.column === 0)
        .reduce((sum, n) => sum + n.value, 0)
    : 0;
  const touches = (l: FlowLink) =>
    hover != null && (l.source === hover || l.target === hover);

  return (
    <div ref={box} className="ds-flow">
      {narrow && (
        <FlowList
          nodes={nodes}
          links={live}
          format={format}
          columns={columns}
          label={label}
        />
      )}
      {layout && (
        <svg
          width={width}
          height={height + head}
          role="img"
          aria-label={label}
          onMouseLeave={() => setHover(null)}
        >
          {columns?.map((c, i) => {
            const n = [...layout.byId.values()].find((x) => x.column === i);
            if (!n) return null;
            const first = i === 0;
            const last = i === layout.columns - 1;
            return (
              <text
                key={c}
                x={first ? n.x + n.w : last ? n.x : n.x + n.w / 2}
                y={12}
                textAnchor={first ? "end" : last ? "start" : "middle"}
                className="ds-flow-head"
              >
                {c}
              </text>
            );
          })}
          <g>
            {layout.paths.map((p) => (
              <path
                key={`${p.link.source}>${p.link.target}`}
                d={p.d}
                className={`ds-flow-link${hover == null ? "" : touches(p.link) ? " on" : " off"}`}
                style={{ fill: p.color }}
              >
                <title>{`${p.from} to ${p.to}: ${format(p.link.value)}`}</title>
              </path>
            ))}
          </g>
          {[...layout.byId.values()].map((n) => (
            <FlowNodeMark
              key={n.id}
              n={n}
              side={
                n.column === 0
                  ? "left"
                  : n.column === layout.columns - 1
                    ? "right"
                    : "inside"
              }
              room={
                n.column === 0
                  ? layout.left
                  : n.column === layout.columns - 1
                    ? layout.right
                    : n.w
              }
              share={total > 0 ? n.value / total : 0}
              format={format}
              dim={hover != null && hover !== n.id && !layout.near(hover, n.id)}
              onHover={setHover}
            />
          ))}
        </svg>
      )}
      {layout && unit && (
        <p className="ds-flow-caption">
          Band width shows {unit}. Hover a name to follow its flows.
        </p>
      )}
    </div>
  );
}

function FlowNodeMark({
  n,
  side,
  room,
  share,
  format,
  dim,
  onHover,
}: {
  n: Placed;
  side: "left" | "right" | "inside";
  room: number;
  share: number;
  format: (v: number) => string;
  dim: boolean;
  onHover: (id: string | null) => void;
}) {
  const cy = n.y + n.h / 2;
  const cls = `ds-flow-node${dim ? " off" : ""}`;
  if (side === "inside") {
    const tx = n.x + 10;
    return (
      <g className={cls} onMouseEnter={() => onHover(n.id)}>
        <title>{`${n.label}: ${format(n.value)}`}</title>
        <rect
          x={n.x}
          y={n.y}
          width={n.w}
          height={n.h}
          rx={6}
          className="ds-flow-block"
          style={{ stroke: n.color ?? "var(--ds-ink-3)" }}
        />
        <text x={tx} y={cy} className="ds-flow-label">
          <tspan x={tx} dy="-0.25em" className="ds-flow-name">
            {fitLabel(n.label, room - 18)}
          </tspan>
          <tspan x={tx} dy="1.3em" className="ds-flow-amount">
            {fitLabel(format(n.value), room - 18)}
          </tspan>
        </text>
      </g>
    );
  }
  const left = side === "left";
  const tx = left ? n.x - 10 : n.x + BAR + 10;
  const anchor = left ? "end" : "start";
  const amount = `${format(n.value)} · ${Math.round(share * 100)}%`;
  const name = fitLabel(n.label, room - 16);
  return (
    <g className={cls} onMouseEnter={() => onHover(n.id)}>
      <title>{`${n.label}: ${amount}`}</title>
      <rect
        x={n.x}
        y={n.y}
        width={BAR}
        height={Math.max(n.h, 2)}
        rx={BAR / 2}
        style={{ fill: n.color ?? "var(--ds-ink-2)" }}
      />
      {n.h >= TWO_LINES ? (
        <text x={tx} y={cy} textAnchor={anchor} className="ds-flow-label">
          <tspan x={tx} dy="-0.25em" className="ds-flow-name">
            {name}
          </tspan>
          <tspan x={tx} dy="1.3em" className="ds-flow-amount">
            {amount}
          </tspan>
        </text>
      ) : (
        <text
          x={tx}
          y={cy}
          dy="0.35em"
          textAnchor={anchor}
          className="ds-flow-label"
        >
          <tspan className="ds-flow-name">
            {fitLabel(n.label, room - 16 - amount.length * CHAR * 0.9)}
          </tspan>
          <tspan className="ds-flow-amount" dx="6">
            {amount}
          </tspan>
        </text>
      )}
    </g>
  );
}

/** Each node's value (the larger of what flows in and out), by column in
 * the order given; nodes nothing flows through are left out. */
function sized(nodes: FlowNode[], links: FlowLink[]) {
  const byId = new Map<string, Sized>();
  for (const n of nodes) byId.set(n.id, { ...n, value: 0, in: 0, out: 0 });
  for (const l of links) {
    const s = byId.get(l.source);
    const t = byId.get(l.target);
    if (!s || !t) continue;
    s.out += l.value;
    t.in += l.value;
  }
  const columns = Math.max(1, ...nodes.map((n) => n.column + 1));
  const cols: Sized[][] = [...Array(columns)].map(() => []);
  for (const n of byId.values()) {
    n.value = Math.max(n.in, n.out);
    if (n.value > 0) cols[n.column].push(n);
    else byId.delete(n.id);
  }
  return { byId, cols, columns };
}

function place(
  nodes: FlowNode[],
  links: FlowLink[],
  width: number,
  height: number,
  top: number,
  format: (v: number) => string,
) {
  const { cols, columns } = sized(nodes, links);
  const isInner = (c: number) => c > 0 && c < columns - 1;
  // Each label column is as wide as its longest name or amount, within limits.
  const want = (c: number) =>
    Math.max(
      0,
      ...cols[c].map(
        (n) =>
          Math.max(
            n.label.length,
            `${format(n.value)}${isInner(c) ? "" : " · 100%"}`.length,
          ) * CHAR,
      ),
    );
  const left = clamp(want(0) + 18, 72, width * 0.24);
  const right =
    columns > 1 ? clamp(want(columns - 1) + 18, 72, width * 0.24) : 0;
  const inner = clamp(
    Math.max(0, ...cols.map((_, c) => (isInner(c) ? want(c) : 0))) + 22,
    96,
    width * 0.2,
  );
  const minOf = (c: number) => (isInner(c) ? MIN_INNER : MIN_OUTER);
  const found = Math.min(
    ...cols
      .map((c, i) =>
        c.length
          ? scaleFor(
              c.map((n) => n.value),
              minOf(i),
              height - GAP * (c.length - 1),
            )
          : Infinity,
      )
      .filter((k) => Number.isFinite(k)),
  );
  const k = found > 0 && Number.isFinite(found) ? found : 1e-9;

  const first = left + BAR / 2;
  const span = width - right - BAR / 2 - first;
  const byId = new Map<string, Placed>();
  cols.forEach((c, i) => {
    const w = isInner(i) ? inner : BAR;
    const centre = columns > 1 ? first + (i * span) / (columns - 1) : first;
    const hs = c.map((n) => Math.max(n.value * k, minOf(i)));
    const used = hs.reduce((s, h) => s + h, 0) + GAP * (c.length - 1);
    let y = top + (height - used) / 2;
    c.forEach((n, j) => {
      const h = hs[j];
      byId.set(n.id, {
        ...n,
        x: centre - w / 2,
        y,
        w,
        h,
        pad: (h - n.value * k) / 2,
      });
      y += h + GAP;
    });
  });

  // Each node's bands stack in the order of the nodes at their other end.
  const kept = links.filter((l) => byId.has(l.source) && byId.has(l.target));
  const ends = (l: FlowLink) => [byId.get(l.source)!, byId.get(l.target)!];
  const y0 = new Map<FlowLink, number>();
  const y1 = new Map<FlowLink, number>();
  const stack = (
    order: (a: FlowLink, b: FlowLink) => number,
    at: (l: FlowLink) => Placed,
    into: Map<FlowLink, number>,
  ) => {
    const used = new Map<string, number>();
    for (const l of [...kept].sort(order)) {
      const n = at(l);
      const off = used.get(n.id) ?? 0;
      into.set(l, n.y + n.pad + off);
      used.set(n.id, off + l.value * k);
    }
  };
  stack(
    (a, b) => ends(a)[0].y - ends(b)[0].y || ends(a)[1].y - ends(b)[1].y,
    (l) => ends(l)[0],
    y0,
  );
  stack(
    (a, b) => ends(a)[1].y - ends(b)[1].y || ends(a)[0].y - ends(b)[0].y,
    (l) => ends(l)[1],
    y1,
  );
  const paths = kept.map((l) => {
    const [s, t] = ends(l);
    const w = l.value * k;
    const a0 = y0.get(l)!;
    const a1 = y1.get(l)!;
    const x0 = s.x + s.w;
    const x1 = t.x;
    const xm = (x0 + x1) / 2;
    return {
      link: l,
      from: s.label,
      to: t.label,
      color: s.color ?? t.color ?? "var(--ds-other)",
      d: `M${x0},${a0}C${xm},${a0} ${xm},${a1} ${x1},${a1}L${x1},${a1 + w}C${xm},${a1 + w} ${xm},${a0 + w} ${x0},${a0 + w}Z`,
    };
  });

  const near = (a: string, b: string) =>
    links.some(
      (l) =>
        (l.source === a && l.target === b) ||
        (l.source === b && l.target === a),
    );
  return { columns, byId, paths, near, left, right };
}

/** The phone form: each column as a ranked list, with the largest flow
 * leaving each node (or, in the last column, arriving at it). */
function FlowList({
  nodes,
  links,
  format,
  columns,
  label,
}: {
  nodes: FlowNode[];
  links: FlowLink[];
  format: (v: number) => string;
  columns?: string[];
  label: string;
}) {
  const { byId, cols } = sized(nodes, links);
  const name = (id: string) => byId.get(id)?.label ?? id;
  const biggest = (n: Sized, last: boolean) => {
    const mine = links.filter((l) =>
      last ? l.target === n.id : l.source === n.id,
    );
    const top = mine.reduce<FlowLink | null>(
      (m, l) => (m == null || l.value > m.value ? l : m),
      null,
    );
    if (!top || mine.length < 2) return null;
    const share = Math.round((top.value / n.value) * 100);
    return last
      ? `${share}% from ${name(top.source)}`
      : `${share}% to ${name(top.target)}`;
  };
  return (
    <div className="ds-flow-list" role="group" aria-label={label}>
      {cols.map((c, i) => {
        if (!c.length) return null;
        const sum = c.reduce((s, n) => s + n.value, 0);
        const last = i === cols.length - 1;
        return (
          <div key={i} className="ds-flow-col">
            {columns?.[i] && (
              <div className="ds-flow-colhead">{columns[i]}</div>
            )}
            {c.map((n) => {
              const sub = biggest(n, last);
              return (
                <div key={n.id} className="ds-flow-item">
                  <span className="ds-flow-item-name" title={n.label}>
                    {n.label}
                  </span>
                  <span className="ds-flow-item-value">
                    {format(n.value)} · {Math.round((n.value / sum) * 100)}%
                  </span>
                  <span className="ds-barlist-track" aria-hidden>
                    <span
                      style={{
                        width: `${Math.max(1, (n.value / sum) * 100)}%`,
                        background: n.color ?? "var(--ds-bar)",
                      }}
                    />
                  </span>
                  {sub && <span className="ds-flow-item-sub">{sub}</span>}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
